<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axiosClient from '../axios'
import type { Faq } from '../types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const faqId = computed(() => route.params.id)
const isEdit = computed(() => !!faqId.value)
const existingFaqs = ref<Faq[]>([])
const nextAvailablePosition = ref(1)
const showPositionWarning = computed(() => faq.position === 999)

const faq = reactive<Partial<Faq>>({
    title: '',
    content: '',
    position: 999
})

const errors = reactive({
    title: '',
    content: ''
})

async function fetchAllFaqs() {
    try {
        const response = await axiosClient.get('/faqs/getAllFaqs')
        if (response.data && response.data.success) {
            existingFaqs.value = response.data.data.data || []
            calculateNextPosition()
        }
    } catch (error) {
        console.error('Error fetching FAQs for position calculation:', error)
    }
}

function calculateNextPosition() {
    if (existingFaqs.value.length === 0) {
        nextAvailablePosition.value = 1
        return
    }
    const positions = existingFaqs.value.map(f => f.position || 999)
    let pos = 1
    while (positions.includes(pos)) {
        pos++
    }
    nextAvailablePosition.value = pos
}

function useNextAvailablePosition() {
    faq.position = nextAvailablePosition.value
}

onMounted(async () => {
    await fetchAllFaqs()
    if (isEdit.value) {
        await fetchFaq()
    }
})

async function fetchFaq() {
    loading.value = true
    try {
        const response = await axiosClient.get(`/faqs/${faqId.value}/get`)
        if (response.data && response.data.success) {
            const faqData = response.data.data
            faq.title = faqData.title
            faq.content = faqData.content
            faq.position = faqData.position
        }
    } catch (error) {
        console.error('Error fetching FAQ:', error)
        router.push({ name: 'Zoznam FAQ' })
    } finally {
        loading.value = false
    }
}

function validateForm() {
    let isValid = true
    errors.title = ''
    errors.content = ''
    if (!faq.title) {
        errors.title = 'Názov FAQ je povinný'
        isValid = false
    }
    if (!faq.content) {
        errors.content = 'Obsah FAQ je povinný'
        isValid = false
    }
    return isValid
}

async function saveFaq() {
    if (!validateForm()) {
        return
    }
    loading.value = true
    try {
        const faqData: Record<string, string | number | undefined> = {
            title: faq.title || '',
            content: faq.content || ''
        }
        if (faq.position !== null && faq.position !== undefined) {
            faqData.position = Number(faq.position)
        }
        let response
        if (isEdit.value) {
            response = await axiosClient.put(`/faqs/${faqId.value}/update`, faqData)
        } else {
            response = await axiosClient.post('/faqs/add', faqData)
        }
        if (response.data) {
            router.push({ name: 'Zoznam FAQ' })
        }
    } catch (error) {
        console.error('Error saving FAQ:', error)
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <main class="flex-1 overflow-y-auto focus:outline-none">
        <div class="py-6">
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <PageBreadcrumb :page-title="isEdit ? 'Upraviť FAQ' : 'Pridať nové FAQ'" />
            </div>
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <div class="py-4">
                    <form @submit.prevent="saveFaq" class="space-y-8 divide-y divide-gray-200 dark:divide-gray-800">
                        <div class="space-y-6">
                            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div class="sm:col-span-6">
                                    <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Názov</label>
                                    <div class="mt-1">
                                        <Input id="title" v-model="faq.title"
                                            class="h-11 w-full rounded-[10px] border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                                            placeholder="Zadajte názov FAQ" />
                                        <p v-if="errors.title" class="mt-2 text-sm text-red-600">{{ errors.title }}</p>
                                    </div>
                                </div>
                                <div class="sm:col-span-6">
                                    <label for="content" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Obsah</label>
                                    <div class="mt-1">
                                        <FormKit type="textarea" id="content" v-model="faq.content" name="content" rows="5"
                                            :classes="{ input: 'block w-full rounded-[10px] border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }"
                                            placeholder="Zadajte obsah FAQ" />
                                        <p v-if="errors.content" class="mt-2 text-sm text-red-600">{{ errors.content }}</p>
                                    </div>
                                </div>
                                <div class="sm:col-span-2">
                                    <label for="position" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Pozícia (nepovinné)</label>
                                    <div class="mt-1 flex items-center space-x-2">
                                        <FormKit type="text" number id="position" v-model="faq.position" name="position" min="1" max="999"
                                            :classes="{ input: 'h-11 w-full rounded-[10px] border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }"
                                            placeholder="1, 2, 3, ..." />
                                        <Button type="button" variant="outline" size="sm" @click="useNextAvailablePosition">
                                            Použiť #{{ nextAvailablePosition }}
                                        </Button>
                                    </div>
                                    <p class="mt-2 text-sm" :class="showPositionWarning ? 'text-amber-600' : 'text-gray-500 dark:text-gray-400'">
                                        <span v-if="showPositionWarning">
                                            <strong>Upozornenie:</strong> Viacero FAQ s rovnakou pozíciou (999) bude zoradených podľa dátumu (novšie hore).
                                        </span>
                                        <span v-else>
                                            Číslo určujúce poradie FAQ (1 je prvé). Ak nevyplníte, bude FAQ zobrazené na konci zoznamu.
                                        </span>
                                    </p>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Odporúčaná: #{{ nextAvailablePosition }} (nasledujúce dostupné číslo)
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="pt-5">
                            <div class="flex justify-end">
                                <Button type="button" variant="outline" @click="router.push({ name: 'Zoznam FAQ' })">Zrušiť</Button>
                                <Button type="submit" class="ml-3" :disabled="loading">
                                    <span v-if="loading">Ukladá sa...</span>
                                    <span v-else>Uložiť</span>
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
/* Using global styles and Tailwind classes */
</style>
